import type { FC, ReactNode } from 'react'
import type { TrackId } from '@/constants/studio'

import { CalendarCheck, HeartHandshake, Users } from 'lucide-react'
import { MaskMark } from '@/components/generals/mask-mark'
import { Reveal } from '@/components/generals/reveal'
import TrialForm from '@/components/landing/trial-form'

interface TrialSectionProps {
    track: TrackId | ''
    onTrackChange: (track: TrackId | '') => void
    focusSignal: number
}

const PERKS = [
    { text: 'שיעור אמיתי עם הקבוצה, לא הדגמה', icon: Users },
    { text: 'התאמת קבוצה לפי גיל וניסיון', icon: CalendarCheck },
    { text: 'בלי התחייבות להמשך', icon: HeartHandshake }
]

const TrialSection: FC<TrialSectionProps> = ({ track, onTrackChange, focusSignal }): ReactNode => {
    return (
        <section
            id='trial'
            aria-labelledby='trial-title'
            className='relative isolate scroll-mt-16 overflow-clip border-t border-border py-20 sm:py-28'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,oklch(0.29_0.1_22/0.5),transparent_70%)]' />

            <div className='mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16'>
                <Reveal className='lg:sticky lg:top-28'>
                    <div className='flex items-end'>
                        <MaskMark variant='tragedy' tone='silver' className='size-14 translate-y-1 rotate-[14deg] opacity-90' />
                        <MaskMark variant='comedy' className='-ms-5 size-16 -rotate-[10deg] drop-shadow-[0_12px_20px_rgba(0,0,0,0.5)]' />
                    </div>

                    <p className='mt-6 text-sm font-medium text-primary'>
                        בואו להתנסות
                    </p>

                    <h2 id='trial-title' className='mt-3 font-heading text-4xl font-bold leading-tight text-balance sm:text-5xl'>
                        הרשמה לשיעור ניסיון
                    </h2>

                    <p className='mt-5 max-w-md text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        השאירו פרטים ונחזור אליכם לתיאום שיעור ניסיון בקבוצה שמתאימה לילד או לילדה שלכם.
                    </p>

                    <ul className='mt-8 space-y-4'>
                        {PERKS.map((perk) => {
                            const Icon = perk.icon

                            return (
                                <li key={perk.text} className='flex items-center gap-3.5 text-foreground/90'>
                                    <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                                        <Icon aria-hidden='true' className='size-5' />
                                    </span>
                                    {perk.text}
                                </li>
                            )
                        })}
                    </ul>
                </Reveal>

                <Reveal delay={120}>
                    <div className='rounded-3xl border border-border bg-card/80 p-6 shadow-2xl shadow-black/40 backdrop-blur-sm sm:p-9'>
                        <TrialForm track={track} onTrackChange={onTrackChange} focusSignal={focusSignal} />
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default TrialSection
